import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Copy,
  RefreshCw,
  Wallet,
  Check,
  CreditCard,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cpnService } from '@/services/cpn'
import { copyToClipboard, formatCurrency, formatDate } from '@/utils/payment'

import { AmountInCad } from '../ui/CurrencyComponents'
import { TotalFeeInCad } from '../ui/TotalFeeInCad'

interface TransactionStatusScreenProps {
  txId: string
  onBack: () => void
}

const TransactionStatusScreen = ({
  txId,
  onBack,
}: TransactionStatusScreenProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Use React Query for efficient data fetching with caching and error handling
  const {
    data: paymentData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['paymentStatus', txId],
    queryFn: () => cpnService.getPaymentStatusWithIntentUUID(txId), // Use payment status endpoint
    enabled: !!txId, // Only run if txId exists
    staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnReconnect: false, // Don't refetch on network reconnect
    refetchInterval: false, // Completely disable auto-refetching
    retry: false, // Disable automatic retries to avoid spam
  })

  // Extract payment and transaction data
  const payment = paymentData?.payment
  const cadAmount = paymentData?.cadAmount

  const handleRefresh = () => {
    refetch()
  }

  const handleCopy = async (text: string, fieldName: string) => {
    const success = await copyToClipboard(text)
    if (success) {
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
    }
  }

  const getStatusInfo = (status: string) => {
    const normalizedStatus = status.toUpperCase()
    switch (normalizedStatus) {
      case 'COMPLETED':
        return {
          icon: <CheckCircle className="w-6 h-6 text-green-600" />,
          label: 'Completed',
          bgColor: 'bg-green-100 text-green-800 border-green-300',
          description: 'Transaction has been successfully completed',
        }
      case 'CREATED':
        return {
          icon: <Clock className="w-6 h-6 text-yellow-600" />,
          label: 'Created',
          bgColor: 'bg-yellow-100 text-yellow-800 border-yellow-300',
          description: 'Payment has been created and is awaiting processing',
        }
      case 'CRYPTO_FUNDS_PENDING':
        return {
          icon: <Clock className="w-6 h-6 text-blue-600" />,
          label: 'Processing',
          bgColor: 'bg-blue-100 text-blue-800 border-blue-300',
          description: 'Waiting for crypto funds confirmation',
        }
      case 'FIAT_PAYMENT_INITIATED':
        return {
          icon: <Clock className="w-6 h-6 text-blue-600" />,
          label: 'Processing',
          bgColor: 'bg-blue-100 text-blue-800 border-blue-300',
          description: 'Payment is being processed',
        }
      case 'FAILED':
        return {
          icon: <XCircle className="w-6 h-6 text-red-600" />,
          label: 'Failed',
          bgColor: 'bg-red-100 text-red-800 border-red-300',
          description: 'Transaction failed to complete',
        }
      default:
        return {
          icon: <AlertCircle className="w-6 h-6 text-gray-600" />,
          label: status,
          bgColor: 'bg-gray-100 text-gray-800 border-gray-300',
          description: 'Transaction status',
        }
    }
  }

  if (isLoading) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading transaction status...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-[600px] flex flex-col bg-white dark:bg-gray-900">
        <div className="p-4 flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </Button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white">
            Payment Status
          </h1>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="p-8 text-center max-w-md mx-auto border-none">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Unable to Find Your Payment
            </h3>

            <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
              We're having trouble locating your payment details. This could be
              due to a temporary system issue or the payment ID may not be
              valid.
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-3">
                Contact support:
              </p>
              <a
                href="mailto:support@paytrie.com"
                className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                support@paytrie.com
              </a>
            </div>

            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleRefresh}
                disabled={isRefetching}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isRefetching ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Checking Again...</span>
                  </div>
                ) : (
                  'Try Again'
                )}
              </Button>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Intent ID: <span className="font-mono">{txId}</span>
              </p>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  if (!payment) {
    return null // This will be handled by isLoading
  }

  const statusInfo = getStatusInfo(payment.status)
  const isCompleted = payment.status.toUpperCase() === 'COMPLETED'

  return (
    <div className="h-[600px] flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white hover:bg-white/20 p-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold">Transaction Status</h1>
          </div>

          {!isCompleted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefetching}
              className="text-white hover:bg-white/20 px-3 py-2 flex items-center space-x-2"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`}
              />
              <span className="text-sm hidden sm:inline">
                {isRefetching ? 'Refreshing...' : 'Refresh'}
              </span>
            </Button>
          )}
        </div>

        {/* Status Overview */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-lg truncate">
                {cadAmount && payment.fromCurrency === 'USDC' ? (
                  <>
                    {formatCurrency(cadAmount, 'CAD')} →{' '}
                    {formatCurrency(payment.toAmount, payment.toCurrency)}
                  </>
                ) : (
                  <>
                    <AmountInCad
                      usdcAmount={payment.fromAmount}
                      originalCurrency={payment.fromCurrency}
                      fallbackToHook={true}
                    />{' '}
                    → {formatCurrency(payment.toAmount, payment.toCurrency)}
                  </>
                )}
              </p>
              <p className="text-blue-200 text-xs">
                Created {formatDate(payment.createdAt)}
              </p>
            </div>
            <div className="flex-shrink-0 self-start sm:self-auto">
              <span
                className={`inline-flex items-center px-2 py-2 rounded-full text-xs font-semibold border ${statusInfo.bgColor}`}
              >
                {statusInfo.icon}
                <span className="ml-1">{statusInfo.label}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Details Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {/* Completion Notice */}
          {isCompleted && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                    Transaction Completed Successfully
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Your payment has been processed and completed.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Transaction Information */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <span>Transaction Information</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* From Amount */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  From Amount
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {cadAmount && payment.fromCurrency === 'USDC' ? (
                      formatCurrency(cadAmount, 'CAD')
                    ) : (
                      <AmountInCad
                        usdcAmount={payment.fromAmount}
                        originalCurrency={payment.fromCurrency}
                        fallbackToHook={true}
                      />
                    )}
                  </span>
                </div>
              </div>

              {/* To Amount */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  To Amount
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(payment.toAmount, payment.toCurrency)}
                  </span>
                </div>
              </div>

              {/* Fee */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Fee
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    <TotalFeeInCad
                      providerFeeAmount={payment.feeAmount}
                      sendAmount={payment.fromAmount}
                      originalCurrency={payment.fromCurrency}
                      fallbackToHook={!cadAmount}
                      platformFeePercentage={0.6}
                      cadAmount={cadAmount}
                    />
                  </span>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Payment Method
                </p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {payment.paymentMethod}
                  </span>
                </div>
              </div>

              {/* Blockchain */}
              {/* <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Blockchain</p>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {payment.blockchain}
                  </span>
                </div>
              </div> */}
            </div>
          </Card>

          {/* Payment Details */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              <span>Payment Details</span>
            </h3>

            <div className="space-y-4">
              {/* Intent ID but we show the user as Transaction ID */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Transaction ID
                </p>
                <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                  <span className="flex-1 font-medium text-gray-900 dark:text-gray-100 font-mono text-sm break-all">
                    {txId}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(txId, 'intentId')}
                    className="flex-shrink-0 p-2 hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Copy to clipboard"
                  >
                    {copiedField === 'intentId' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Settlement Time - Only show if not failed or completed */}
              {payment.status.toUpperCase() !== 'FAILED' &&
                payment.status.toUpperCase() !== 'COMPLETED' && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Settlement Time
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {payment.fiatSettlementMin}-{payment.fiatSettlementMax}{' '}
                        {payment.fiatSettlementUnit.toLowerCase()}
                      </span>
                    </div>
                  </div>
                )}

              {/* Transactions */}
              {/* {transactions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Transactions</p>
                  {transactions.map((tx, index) => (
                    <div key={tx.id || index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {formatCurrency(tx.amount, tx.currency)}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          tx.status.toLowerCase() === 'completed' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                      {tx.txHash && (
                        <div className="flex items-center space-x-2">
                          <span className="flex-1 font-mono text-xs text-gray-600 dark:text-gray-400 break-all">
                            {tx.txHash}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(tx.txHash, `txHash-${index}`)}
                            className="flex-shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                            title="Copy transaction hash"
                          >
                            {copiedField === `txHash-${index}` ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )} */}
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Secured by Paytrie • Circle Payments Network
          </p>
        </div>
      </div>
    </div>
  )
}

export default TransactionStatusScreen
